/**
 * API Service
 * Supabase-only data layer
 *
 * This service handles all API operations using Supabase as the sole data source.
 * No mock data fallbacks - database is the source of truth.
 *
 * Features:
 * - Distributed tracing via X-Trace-ID header
 * - Error logging with trace correlation
 */

import { supabase } from './supabase';
import { isSchemasCacheError } from './supabaseErrors';

// ============================================
// DISTRIBUTED TRACING
// ============================================

/**
 * Generate a unique trace ID for request correlation
 */
export function generateTraceId(): string {
  return crypto.randomUUID();
}

/**
 * Current trace ID for the active request context
 * Used for error correlation and logging
 */
let currentTraceId: string | null = null;

/**
 * Get the current trace ID or generate a new one
 */
export function getOrCreateTraceId(): string {
  if (!currentTraceId) {
    currentTraceId = generateTraceId();
  }
  return currentTraceId;
}

/**
 * Clear the current trace ID (call after request completes)
 */
export function clearTraceId(): void {
  currentTraceId = null;
}

/**
 * Log an error with trace ID for correlation
 */
export function logErrorWithTrace(context: string, error: unknown): void {
  const traceId = getOrCreateTraceId();
  const errorMessage = error instanceof Error ? error.message : String(error);
  console.error(`[TraceID: ${traceId}] [${context}] ${errorMessage}`);
}
import {
  Alert,
  Case,
  Task,
  StoreZone,
  ZoneKPI,
  ActivityLog,
  // v3.0.0 Types
  Supplier,
  SupplierCreateInput,
  SupplierUpdateInput,
  SupplierPerformanceMetrics,
  ReliabilityGrade,
  PurchaseOrder,
  POCreateInput,
  POLineItem,
  ReceivingLog,
  ReceivingInput,
  POStatus,
  BusinessRule,
  RuleCreateInput,
  RuleExecution,
  RuleCategory,
  RuleTriggerEvent,
  ABCClass,
  ABCClassificationConfig,
  ABCClassificationSummary,
  ABCClassificationResult,
  ProductWithABC,
  StockoutEvent,
  StockoutPrediction,
  HighRiskStockoutPrediction,
  ActiveStockout,
  StockoutPredictionResult,
  StockoutRootCause,
  AnomalyScore,
  AnomalyEntityType,
  AnomalyInvestigationStatus,
  DemandForecast,
  InventoryHealthMetrics,
  DateRange,
} from '../types';
import { DEFAULT_ZONE_TEMPLATES, type ZoneCategory } from './zoneManagement';
import {
  masterDataService,
  type Store,
  type StoreCreateInput,
  type StoreUpdateInput,
  type Product,
  type ProductCreateInput,
  type ProductUpdateInput,
  type Employee,
  type EmployeeCreateInput,
  type EmployeeUpdateInput,
  type PaginationParams,
  type FilterParams,
  type PaginatedResult,
} from './masterData';

// v3.0.0 Services
import { supplierService, type SupplierPaginationParams, type SupplierFilterParams } from './supplierService';
import { purchaseOrderService, type POPaginationParams, type POFilterParams } from './purchaseOrderService';
import { rulesEngineService } from './_legacy/rulesEngineService';
import { abcClassificationService } from './abcClassificationService';
import { stockoutPredictionService } from './stockoutPredictionService';
import { anomalyDetectionService, type AnomalyEntityType as AnomalyEntityTypeImport } from './_legacy/anomalyDetectionService';
import { TaskRepository } from './repositories/TaskRepository';

// Re-export master data types
export type {
  Store,
  StoreCreateInput,
  StoreUpdateInput,
  Product,
  ProductCreateInput,
  ProductUpdateInput,
  Employee,
  EmployeeCreateInput,
  EmployeeUpdateInput,
  PaginationParams,
  FilterParams,
  PaginatedResult,
};

export const api = {
    // ============================================
    // STORES (Master Data - Real DB)
    // ============================================

    getStores: async (
      pagination?: PaginationParams,
      filters?: FilterParams
    ): Promise<PaginatedResult<Store>> => {
        return masterDataService.stores.getStores(pagination, filters);
    },

    getStore: async (id: string): Promise<Store | null> => {
        return masterDataService.stores.getStore(id);
    },

    getStoreByCode: async (code: string): Promise<Store | null> => {
        return masterDataService.stores.getStoreByCode(code);
    },

    createStore: async (input: StoreCreateInput, tenantId: string): Promise<Store> => {
        return masterDataService.stores.createStore(input, tenantId);
    },

    updateStore: async (id: string, input: StoreUpdateInput): Promise<Store> => {
        return masterDataService.stores.updateStore(id, input);
    },

    deleteStore: async (id: string, hardDelete = false): Promise<boolean> => {
        return masterDataService.stores.deleteStore(id, hardDelete);
    },

    getStoreStats: async () => {
        return masterDataService.stores.getStoreStats();
    },

    // ============================================
    // PRODUCTS (Master Data - Real DB)
    // ============================================

    getProducts: async (
      pagination?: PaginationParams,
      filters?: FilterParams
    ): Promise<PaginatedResult<Product>> => {
        return masterDataService.products.getProducts(pagination, filters);
    },

    getProduct: async (id: string): Promise<Product | null> => {
        return masterDataService.products.getProduct(id);
    },

    getProductBySku: async (sku: string): Promise<Product | null> => {
        return masterDataService.products.getProductBySku(sku);
    },

    createProduct: async (input: ProductCreateInput, tenantId: string): Promise<Product> => {
        return masterDataService.products.createProduct(input, tenantId);
    },

    updateProduct: async (id: string, input: ProductUpdateInput): Promise<Product> => {
        return masterDataService.products.updateProduct(id, input);
    },

    deleteProduct: async (id: string, hardDelete = false): Promise<boolean> => {
        return masterDataService.products.deleteProduct(id, hardDelete);
    },

    getProductCategories: async (): Promise<string[]> => {
        return masterDataService.products.getCategories();
    },

    getHighRiskProducts: async (limit = 20): Promise<Product[]> => {
        return masterDataService.products.getHighRiskProducts(limit);
    },

    // ============================================
    // EMPLOYEES (Master Data - Real DB)
    // ============================================

    getEmployees: async (
      pagination?: PaginationParams,
      filters?: FilterParams
    ): Promise<PaginatedResult<Employee>> => {
        return masterDataService.employees.getEmployees(pagination, filters);
    },

    getEmployee: async (id: string): Promise<Employee | null> => {
        return masterDataService.employees.getEmployee(id);
    },

    getEmployeesByStore: async (storeId: string): Promise<Employee[]> => {
        return masterDataService.employees.getEmployeesByStore(storeId);
    },

    createEmployee: async (input: EmployeeCreateInput, tenantId: string): Promise<Employee> => {
        return masterDataService.employees.createEmployee(input, tenantId);
    },

    updateEmployee: async (id: string, input: EmployeeUpdateInput): Promise<Employee> => {
        return masterDataService.employees.updateEmployee(id, input);
    },

    terminateEmployee: async (id: string, terminationDate?: string): Promise<Employee> => {
        return masterDataService.employees.terminateEmployee(id, terminationDate);
    },

    deleteEmployee: async (id: string): Promise<boolean> => {
        return masterDataService.employees.deleteEmployee(id);
    },

    getHighRiskEmployees: async (threshold = 70, limit = 20): Promise<Employee[]> => {
        return masterDataService.employees.getHighRiskEmployees(threshold, limit);
    },

    getEmployeeStats: async (storeId?: string) => {
        return masterDataService.employees.getEmployeeStats(storeId);
    },

    // ============================================
    // TASKS (Real DB)
    // ============================================

    /**
     * Get tasks filtered by tenant and optionally by store.
     * SECURITY: tenantId is required to prevent cross-tenant data leakage.
     * @param tenantId - Required tenant UUID for security filtering
     * @param storeId - Optional store UUID to filter by
     */
    getTasks: async (tenantId: string, storeId?: string): Promise<Task[]> => {
        // SECURITY: Require tenantId to prevent data leakage
        if (!tenantId) {
            console.error('[api.getTasks] tenantId is required for security');
            return [];
        }

        let query = supabase
            .from('tasks')
            .select('*, assignee_user:users!assignee_id(id, full_name, email)')
            .eq('tenant_id', tenantId)  // SECURITY: Always filter by tenant
            .order('created_at', { ascending: false })
            .limit(500);

        if (storeId) {
            query = query.eq('store_id', storeId);
        }

        const { data, error } = await query;
        if (error) {
            if (isSchemasCacheError(error)) {
                console.debug('Tasks table not available (demo mode)');
            } else {
                console.error('Failed to fetch tasks:', error);
            }
            return [];
        }

        // Map snake_case DB fields to camelCase Task interface
        // Also map old schema statuses to new schema if needed
        const statusMap: Record<string, string> = {
            'pending': 'todo',
            'completed': 'done',
            'cancelled': 'rejected',
        };

        return (data || []).map((row: any) => ({
            id: row.id,
            title: row.title,
            description: row.description || '',
            assigneeId: row.assignee_id || undefined,
            assignee: row.assignee_user?.full_name || row.assignee_user?.email || 'Sin Asignar',
            storeId: row.store_id || undefined,
            priority: row.priority || 'medium',
            status: statusMap[row.status] || row.status || 'todo',
            dueDate: row.due_date ? row.due_date.split('T')[0] : '',
            type: row.type || 'inventory',
            targetRole: row.target_role ?? undefined,
            sourceRuleId: row.source_rule_id ?? undefined,
            reviewInfo: row.review_info ? {
                reviewedBy: row.review_info.reviewed_by,
                reviewedAt: row.review_info.reviewed_at,
                reviewComment: row.review_info.review_comment,
                rejectionReason: row.review_info.rejection_reason,
            } : undefined,
            submittedForReviewAt: row.submitted_for_review_at,
            submittedForReviewBy: row.submitted_for_review_by,
        })) as Task[];
    },

    // Task write-path functions delegate to TaskRepository (v2 refactor PR #3).
    // These wrappers stay to preserve the api.foo(...) call surface used across
    // the app; they will be removed in a follow-up cleanup pass once callers
    // import TaskRepository directly.
    createTask: (task: Task, tenantId: string, storeId?: string): Promise<Task> =>
        TaskRepository.createTask(task, tenantId, storeId),

    updateTaskStatus: (id: string, tenantId: string, status: Task['status']): Promise<void> =>
        TaskRepository.updateTaskStatus(id, tenantId, status),

    updateTaskAssignee: (id: string, tenantId: string, assigneeId: string | null): Promise<void> =>
        TaskRepository.updateTaskAssignee(id, tenantId, assigneeId),

    submitTaskForReview: (id: string, tenantId: string, submittedBy: string): Promise<void> =>
        TaskRepository.submitTaskForReview(id, tenantId, submittedBy),

    approveTask: (id: string, tenantId: string, reviewedBy: string, comment?: string): Promise<void> =>
        TaskRepository.approveTask(id, tenantId, reviewedBy, comment),

    rejectTask: (id: string, tenantId: string, reviewedBy: string, rejectionReason: string): Promise<void> =>
        TaskRepository.rejectTask(id, tenantId, reviewedBy, rejectionReason),

    /**
     * Get alerts filtered by tenant and optionally by store.
     * SECURITY: tenantId is required to prevent cross-tenant data leakage.
     * NOTE: Prefer using AlertRepository.getAlerts() which has Zod validation.
     * @param tenantId - Required tenant UUID for security filtering
     * @param storeId - Optional store UUID to filter by
     */
    getAlerts: async (tenantId: string, storeId?: string): Promise<Alert[]> => {
        // SECURITY: Require tenantId to prevent data leakage
        if (!tenantId) {
            console.error('[api.getAlerts] tenantId is required for security');
            return [];
        }

        let query = supabase
            .from("alerts")
            .select("*")
            .eq("tenant_id", tenantId)  // SECURITY: Always filter by tenant
            .order("created_at", { ascending: false });

        if (storeId) {
            query = query.eq("store_id", storeId);
        }

        const { data, error } = await query;
        if (error) {
            if (isSchemasCacheError(error)) {
                console.debug('Alerts table not available (demo mode)');
            } else {
                console.error('Failed to fetch alerts:', error);
            }
            return [];
        }
        return (data || []).map((row: any) => ({
            ...row,
            storeId: row.store_id,
            timestamp: row.created_at,
            potentialImpact: row.potential_impact ?? row.potentialImpact,
            estimatedValue: row.estimated_value ?? row.estimatedValue,
            assignee: row.assignee_id ?? row.assignee,
        })) as Alert[];
    },

    /**
     * Get cases filtered by tenant and optionally by store.
     * SECURITY: tenantId is required to prevent cross-tenant data leakage.
     * @param tenantId - Required tenant UUID for security filtering
     * @param storeId - Optional store UUID to filter by
     */
    getCases: async (tenantId: string, storeId?: string): Promise<Case[]> => {
        // SECURITY: Require tenantId to prevent data leakage
        if (!tenantId) {
            console.error('[api.getCases] tenantId is required for security');
            return [];
        }

        let query = supabase
            .from('cases')
            // Linked alerts live in the case_alerts join table (cases has no alert column)
            .select('*, case_alerts(alert_id)')
            .eq('tenant_id', tenantId)  // SECURITY: Always filter by tenant
            .order('created_at', { ascending: false });

        if (storeId) {
            query = query.eq('store_id', storeId);
        }

        const { data, error } = await query;

        if (error) {
            if (isSchemasCacheError(error)) {
                console.debug('Cases table not available (demo mode)');
            } else {
                console.error('Failed to fetch cases:', error);
            }
            return [];
        }
        return (data || []).map(({ case_alerts, ...row }: any) => ({
            ...row,
            storeId: row.store_id ?? undefined,
            investigatorId: row.investigator_id ?? null,
            relatedAlertIds: (case_alerts ?? []).map((l: { alert_id: string }) => l.alert_id),
            createdDate: row.created_at ? new Date(row.created_at).toISOString().split('T')[0] : '',
        })) as Case[];
    },

    createCase: async (newCase: Case, tenantId: string): Promise<void> => {
        if (!tenantId) {
            throw new Error('Tenant ID is required to create a case');
        }

        // investigator_id is a users.id UUID (Case.investigator is only a display name).
        // Alert links are written by AlertRepository.linkAlertToCase (case_alerts).
        const { error } = await supabase
            .from('cases')
            .insert({
                id: newCase.id,
                title: newCase.title,
                summary: newCase.summary,
                status: newCase.status,
                investigator_id: newCase.investigatorId ?? null,
                store_id: newCase.storeId ?? null,
                tenant_id: tenantId
            });

        if (error) {
            console.error('Failed to create case:', error);
            throw error;
        }
    },

    updateCase: async (id: string, updates: Partial<Case>, tenantId: string): Promise<void> => {
        if (!tenantId) {
            throw new Error('Tenant ID is required to update a case');
        }
        // Only columns that exist on cases — UI-only fields (createdDate, investigator
        // display name, relatedAlertIds) would make PostgREST reject the whole update.
        const dbUpdates: Record<string, unknown> = {};
        if (updates.title !== undefined) dbUpdates.title = updates.title;
        if (updates.summary !== undefined) dbUpdates.summary = updates.summary;
        if (updates.status !== undefined) {
            dbUpdates.status = updates.status;
            // closed_at records when the case was closed: set it only on a move into
            // 'closed', clear it only on a move out. Callers resend the status on every
            // save, so re-saving a closed case must keep the original timestamp.
            const { data: current, error: readError } = await supabase
                .from('cases')
                .select('status')
                .eq('id', id)
                .eq('tenant_id', tenantId)
                .maybeSingle();
            if (readError) {
                console.error('Failed to read case status:', readError);
                throw readError;
            }
            const wasClosed = current?.status === 'closed';
            const isClosed = updates.status === 'closed';
            if (isClosed !== wasClosed) {
                dbUpdates.closed_at = isClosed ? new Date().toISOString() : null;
            }
        }
        if ('investigatorId' in updates) dbUpdates.investigator_id = updates.investigatorId ?? null;
        if ('storeId' in updates) dbUpdates.store_id = updates.storeId ?? null;
        if (Object.keys(dbUpdates).length === 0) return;

        const { error } = await supabase
            .from('cases')
            .update(dbUpdates)
            .eq('id', id)
            .eq('tenant_id', tenantId);

        if (error) {
            console.error('Failed to update case:', error);
            throw error;
        }
    },

    // --- STORE ZONES ---
    /**
     * Get store zones with metrics.
     * SECURITY: tenantId is required to prevent cross-tenant data leakage.
     * @param tenantId - Required tenant UUID for security filtering
     * @param storeId - The store UUID
     */
    getStoreZones: async (tenantId: string, storeId: string): Promise<StoreZone[]> => {
        // SECURITY: Require tenantId to prevent data leakage
        if (!tenantId) {
            console.error('[api.getStoreZones] tenantId is required for security');
            return [];
        }

        // Fetch zones from store_zones table
        // SECURITY: Filter by tenant_id for defense-in-depth
        const { data: zones, error: zonesError } = await supabase
            .from('store_zones')
            .select('*')
            .eq('tenant_id', tenantId)  // SECURITY: Always filter by tenant
            .eq('store_id', storeId);

        if (zonesError) {
            // Schema cache errors are expected in demo mode - log quietly
            if (isSchemasCacheError(zonesError)) {
                console.debug('Store zones table not available (demo mode)');
            } else {
                console.error('Failed to fetch store zones:', zonesError);
            }
            return [];
        }
        if (!zones || zones.length === 0) {
            return [];
        }

        // Fetch metrics from zone_metrics table
        // SECURITY: Filter by tenant_id for defense-in-depth
        const { data: metrics } = await supabase
            .from('zone_metrics')
            .select('*')
            .eq('tenant_id', tenantId)  // SECURITY: Always filter by tenant
            .eq('store_id', storeId);

        // Map database records to StoreZone type
        return zones.map(zone => {
            const zoneMetric = metrics?.find(m => m.zone_id === zone.id);
            const gridPos = zone.grid_position as { row: string; col: string } | null;
            const category = zone.category as ZoneCategory;
            return {
                id: zone.id,
                name: zone.name,
                gridPosition: gridPos || { row: '1 / 2', col: '1 / 2' },
                riskScore: zoneMetric?.risk_score || 0,
                topIssue: zoneMetric?.top_issue || 'Normal',
                kpis: (zoneMetric?.kpis as ZoneKPI[] | null) || [],
                staffOnDuty: zoneMetric?.staff_on_duty || 0,
                cameraStatus: (zoneMetric?.camera_status || 'nominal') as 'nominal' | 'warning' | 'error',
                activeIncidents: zoneMetric?.active_incidents || 0,
                recentActivity: (zoneMetric?.recent_activity as ActivityLog[] | null) || [],
                suggestedActions: DEFAULT_ZONE_TEMPLATES[category]?.suggestedActions || []
            };
        });
    },

    updateStoreZone: async (zoneId: string, updates: { name?: string; category?: string; gridPosition?: { row: string; col: string } }): Promise<void> => {
        try {
            const dbUpdates: Record<string, unknown> = {};
            if (updates.name) dbUpdates.name = updates.name;
            if (updates.category) dbUpdates.category = updates.category;
            if (updates.gridPosition) dbUpdates.grid_position = updates.gridPosition;
            dbUpdates.updated_at = new Date().toISOString();

            const { error } = await supabase
                .from('store_zones')
                .update(dbUpdates)
                .eq('id', zoneId);

            if (error) throw error;
        } catch (e) {
            console.error('Failed to update zone in Supabase:', e);
        }
    },

    /**
     * Batch update zone grid positions (for drag-and-drop reordering).
     * SECURITY: tenantId is required to prevent cross-tenant data leakage.
     * @param tenantId - Required tenant UUID for security filtering
     * @param updates - Array of zone ID and new position pairs
     * @returns true if all updates succeeded
     */
    updateZonePositions: async (
        tenantId: string,
        updates: Array<{ zoneId: string; gridPosition: { row: string; col: string } }>
    ): Promise<boolean> => {
        // SECURITY: Require tenantId to prevent data leakage
        if (!tenantId) {
            console.error('[api.updateZonePositions] tenantId is required for security');
            return false;
        }

        if (updates.length === 0) {
            return true;
        }

        try {
            // Try RPC function first for atomic update
            const { error: rpcError } = await supabase.rpc('update_zone_positions', {
                p_tenant_id: tenantId,
                p_updates: JSON.stringify(updates.map(u => ({
                    zone_id: u.zoneId,
                    grid_position: u.gridPosition,
                }))),
            });

            if (!rpcError) {
                return true;
            }

            // Fallback to individual updates if RPC not available
            if (rpcError.code === '42883' || rpcError.message?.includes('does not exist')) {
                console.warn('[api.updateZonePositions] RPC not available, using fallback');

                for (const update of updates) {
                    const { error } = await supabase
                        .from('store_zones')
                        .update({
                            grid_position: update.gridPosition,
                            updated_at: new Date().toISOString(),
                        })
                        .eq('id', update.zoneId)
                        .eq('tenant_id', tenantId); // SECURITY: Always filter by tenant

                    if (error) {
                        console.error(`[api.updateZonePositions] Failed to update zone ${update.zoneId}:`, error);
                        return false;
                    }
                }
                return true;
            }

            console.error('[api.updateZonePositions] RPC error:', rpcError);
            return false;
        } catch (e) {
            logErrorWithTrace('api.updateZonePositions', e);
            return false;
        }
    },

    /**
     * Detect inventory locations that don't have matching zones.
     * SECURITY: tenantId is required to prevent cross-tenant data leakage.
     * @param tenantId - Required tenant UUID for security filtering
     * @param storeId - The store UUID
     * @returns Array of location names without zones
     */
    detectUnmappedZoneLocations: async (
        tenantId: string,
        storeId: string
    ): Promise<string[]> => {
        // SECURITY: Require tenantId to prevent data leakage
        if (!tenantId || !storeId) {
            console.error('[api.detectUnmappedZoneLocations] tenantId and storeId are required');
            return [];
        }

        try {
            // Import lazily to avoid circular dependencies
            const { zoneManagementService } = await import('./zoneManagement');
            return zoneManagementService.detectUnmappedLocations(storeId, tenantId);
        } catch (e) {
            logErrorWithTrace('api.detectUnmappedZoneLocations', e);
            return [];
        }
    },

    // ============================================
    // v3.0.0 - SUPPLIER MANAGEMENT
    // Per CONTEXT.md Section 2.2
    // ============================================

    getSuppliers: async (
        _tenantId: string,
        pagination?: PaginationParams,
        filters?: FilterParams
    ): Promise<PaginatedResult<Supplier>> => {
        return supplierService.getSuppliers(pagination as SupplierPaginationParams, filters as SupplierFilterParams);
    },

    getSupplier: async (id: string): Promise<Supplier | null> => {
        return supplierService.getSupplier(id);
    },

    getSupplierByCode: async (_tenantId: string, code: string): Promise<Supplier | null> => {
        return supplierService.getSupplierByCode(code);
    },

    createSupplier: async (input: SupplierCreateInput, tenantId: string): Promise<Supplier> => {
        return supplierService.createSupplier(input, tenantId);
    },

    updateSupplier: async (id: string, input: SupplierUpdateInput): Promise<Supplier> => {
        return supplierService.updateSupplier(id, input);
    },

    deleteSupplier: async (id: string): Promise<boolean> => {
        return supplierService.deleteSupplier(id);
    },

    getSupplierPerformance: async (
        supplierId: string,
        dateRange: DateRange
    ): Promise<SupplierPerformanceMetrics[]> => {
        return supplierService.getSupplierPerformance(supplierId, dateRange);
    },

    calculateSupplierReliabilityGrade: async (supplierId: string): Promise<ReliabilityGrade> => {
        // Use last 90 days for reliability grade calculation
        const end = new Date().toISOString();
        const start = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString();
        const metrics = await supplierService.getSupplierPerformanceSummary(supplierId, { start, end });
        return supplierService.calculateReliabilityGrade(metrics?.onTimeRate ?? 0);
    },

    getTopPerformingSuppliers: async (_tenantId: string, limit = 10): Promise<Supplier[]> => {
        return supplierService.getTopPerformingSuppliers(limit);
    },

    getUnreliableSuppliers: async (_tenantId: string, threshold = 80): Promise<Supplier[]> => {
        return supplierService.getUnreliableSuppliers(threshold);
    },

    // ============================================
    // v3.0.0 - PURCHASE ORDERS
    // Per CONTEXT.md Section 2.2
    // ============================================

    getPurchaseOrders: async (
        _tenantId: string,
        pagination?: PaginationParams,
        filters?: FilterParams
    ): Promise<PaginatedResult<PurchaseOrder>> => {
        return purchaseOrderService.getPurchaseOrders(_tenantId, pagination as POPaginationParams, filters as POFilterParams);
    },

    getPurchaseOrder: async (id: string): Promise<PurchaseOrder | null> => {
        return purchaseOrderService.getPurchaseOrder(id);
    },

    createPurchaseOrder: async (input: POCreateInput, tenantId: string): Promise<PurchaseOrder> => {
        return purchaseOrderService.createPurchaseOrder(input, tenantId);
    },

    updatePurchaseOrderStatus: async (id: string, status: POStatus): Promise<PurchaseOrder> => {
        return purchaseOrderService.updatePurchaseOrder(id, { status });
    },

    recordReceiving: async (
        input: ReceivingInput,
        receivedBy: string,
        tenantId: string
    ): Promise<ReceivingLog> => {
        return purchaseOrderService.recordReceiving(input, receivedBy, tenantId);
    },

    getReceivingLogs: async (purchaseOrderId: string): Promise<ReceivingLog[]> => {
        return purchaseOrderService.getReceivingLogs(purchaseOrderId);
    },

    getPOsByStatus: async (_tenantId: string, status: POStatus): Promise<PurchaseOrder[]> => {
        return purchaseOrderService.getPOsByStatus(status);
    },

    getOverduePOs: async (_tenantId: string): Promise<PurchaseOrder[]> => {
        return purchaseOrderService.getOverduePOs();
    },

    getShortShippedPOs: async (_tenantId: string, threshold = 5): Promise<PurchaseOrder[]> => {
        return purchaseOrderService.getShortShippedPOs(threshold);
    },

    getPOLineItems: async (purchaseOrderId: string): Promise<POLineItem[]> => {
        return purchaseOrderService.getLineItems(purchaseOrderId);
    },

    // ============================================
    // v3.0.0 - BUSINESS RULES ENGINE
    // Per CONTEXT.md Section 2.2
    // ============================================

    getRules: async (
        tenantId: string,
        category?: RuleCategory
    ): Promise<BusinessRule[]> => {
        const result = await rulesEngineService.getRules(tenantId, undefined, category ? { category } : undefined);
        return result.data;
    },

    getRule: async (ruleId: string): Promise<BusinessRule | null> => {
        return rulesEngineService.getRule(ruleId);
    },

    createRule: async (input: RuleCreateInput, tenantId: string): Promise<BusinessRule> => {
        return rulesEngineService.createRule(input, tenantId);
    },

    updateRule: async (ruleId: string, input: Partial<RuleCreateInput>): Promise<BusinessRule> => {
        return rulesEngineService.updateRule(ruleId, input);
    },

    toggleRule: async (ruleId: string, isActive: boolean): Promise<BusinessRule> => {
        return rulesEngineService.toggleRule(ruleId, isActive);
    },

    deleteRule: async (ruleId: string): Promise<boolean> => {
        return rulesEngineService.deleteRule(ruleId);
    },

    executeRulesForEvent: async (
        tenantId: string,
        triggerEvent: RuleTriggerEvent,
        context: Record<string, unknown>,
        entityType?: string,
        entityId?: string
    ): Promise<RuleExecution[]> => {
        const results = await rulesEngineService.executeRulesForEvent(
            tenantId,
            triggerEvent,
            context,
            entityType,
            entityId
        );
        // Map execution results to RuleExecution type
        return results.map(r => ({
            id: crypto.randomUUID(),
            tenantId,
            ruleId: r.ruleId,
            triggeredAt: new Date().toISOString(),
            triggerEvent,
            triggerData: context,
            conditionsMet: r.triggered,
            actionTaken: r.actionTaken,
            success: r.success,
            errorMessage: r.errorMessage,
            createdAt: new Date().toISOString(),
        }));
    },

    getRuleExecutions: async (
        ruleId: string,
        limit = 50
    ): Promise<RuleExecution[]> => {
        return rulesEngineService.getExecutions(ruleId, limit);
    },

    getRuleTemplates: async () => {
        return rulesEngineService.getTemplates();
    },

    // ============================================
    // v3.0.0 - ABC CLASSIFICATION
    // Per CONTEXT.md Section 2.1
    // ============================================

    classifyProducts: async (
        tenantId: string,
        options?: { storeId?: string; periodDays?: number }
    ): Promise<ABCClassificationResult> => {
        return abcClassificationService.classifyProducts(tenantId, options);
    },

    getProductsByClass: async (
        tenantId: string,
        abcClass: ABCClass,
        options?: { limit?: number }
    ): Promise<ProductWithABC[]> => {
        return abcClassificationService.getProductsByClass(tenantId, abcClass, options?.limit);
    },

    getClassificationSummary: async (tenantId: string): Promise<ABCClassificationSummary | null> => {
        return abcClassificationService.getSummary(tenantId);
    },

    getClassificationConfig: async (tenantId: string): Promise<ABCClassificationConfig | null> => {
        return abcClassificationService.getConfig(tenantId);
    },

    updateClassificationConfig: async (
        tenantId: string,
        config: Partial<ABCClassificationConfig>
    ): Promise<ABCClassificationConfig> => {
        return abcClassificationService.upsertConfig(tenantId, config);
    },

    getClassificationHistory: async (
        productId: string,
        limit = 10
    ) => {
        return abcClassificationService.getProductHistory(productId, limit);
    },

    // ============================================
    // v3.0.0 - STOCKOUT PREDICTION
    // Per CONTEXT.md Section 2.3
    // ============================================

    recordStockout: async (
        tenantId: string,
        productId: string,
        storeId: string,
        options?: {
            estimatedLostSales?: number;
            estimatedLostUnits?: number;
            linkedPoId?: string;
        }
    ): Promise<StockoutEvent> => {
        return stockoutPredictionService.recordStockout(tenantId, productId, storeId, options);
    },

    resolveStockout: async (
        eventId: string,
        resolvedBy: string,
        options?: {
            rootCause?: StockoutRootCause;
            rootCauseNotes?: string;
            resolutionNotes?: string;
        }
    ): Promise<StockoutEvent> => {
        return stockoutPredictionService.resolveStockout(eventId, resolvedBy, options);
    },

    getActiveStockouts: async (
        tenantId: string,
        options?: { storeId?: string; abcClass?: ABCClass; limit?: number }
    ): Promise<ActiveStockout[]> => {
        return stockoutPredictionService.getActiveStockouts(tenantId, options);
    },

    getStockoutEvents: async (
        tenantId: string,
        dateRange: DateRange,
        options?: { storeId?: string; productId?: string; rootCause?: StockoutRootCause }
    ): Promise<StockoutEvent[]> => {
        return stockoutPredictionService.getStockoutEvents(tenantId, dateRange, options);
    },

    predictStockouts: async (
        tenantId: string,
        options?: { storeId?: string; horizonDays?: number; abcClassFilter?: ABCClass[] }
    ): Promise<StockoutPredictionResult> => {
        return stockoutPredictionService.predictStockouts(tenantId, options);
    },

    getHighRiskPredictions: async (
        tenantId: string,
        options?: { storeId?: string; abcClass?: ABCClass; limit?: number }
    ): Promise<HighRiskStockoutPrediction[]> => {
        return stockoutPredictionService.getHighRiskPredictions(tenantId, options);
    },

    getStockoutRate: async (tenantId: string, dateRange: DateRange): Promise<number> => {
        return stockoutPredictionService.calculateStockoutRate(tenantId, dateRange);
    },

    getStockoutRootCauseBreakdown: async (
        tenantId: string,
        dateRange: DateRange
    ) => {
        return stockoutPredictionService.getStockoutRootCauseBreakdown(tenantId, dateRange);
    },

    estimateLostSales: async (tenantId: string, dateRange: DateRange): Promise<number> => {
        return stockoutPredictionService.estimateLostSales(tenantId, dateRange);
    },

    // ============================================
    // v3.0.0 - ANOMALY DETECTION
    // TASK-020: Statistical anomaly detection
    // ============================================

    detectAnomalies: async (
        tenantId: string,
        entityType: AnomalyEntityTypeImport,
        dateRange: DateRange,
        config?: { zScoreThreshold?: number; baselineWindowDays?: number; minSampleSize?: number }
    ) => {
        return anomalyDetectionService.detectAnomalies(tenantId, entityType, dateRange, config);
    },

    getAnomalyScores: async (
        tenantId: string,
        entityType?: AnomalyEntityTypeImport,
        entityId?: string,
        dateRange?: DateRange
    ): Promise<AnomalyScore[]> => {
        return anomalyDetectionService.getAnomalyScores(tenantId, entityType, entityId, dateRange);
    },

    getAnomalySummary: async (
        tenantId: string,
        entityType: AnomalyEntityTypeImport,
        limit?: number
    ) => {
        return anomalyDetectionService.getAnomalySummary(tenantId, entityType, limit);
    },

    runAnomalyScan: async (tenantId: string) => {
        return anomalyDetectionService.runFullScan(tenantId);
    },

    updateAnomalyInvestigation: async (
        anomalyId: string,
        status: AnomalyInvestigationStatus,
        investigatedBy?: string
    ): Promise<AnomalyScore> => {
        return stockoutPredictionService.updateAnomalyInvestigation(anomalyId, status, investigatedBy);
    },

    // ============================================
    // v3.0.0 - DEMAND FORECASTING
    // ============================================

    getDemandForecast: async (
        productId: string,
        options?: { storeId?: string; limit?: number }
    ): Promise<DemandForecast[]> => {
        return stockoutPredictionService.getDemandForecast(productId, options);
    },

    createDemandForecast: async (
        tenantId: string,
        productId: string,
        storeId: string | null,
        horizonDays?: number
    ): Promise<DemandForecast> => {
        return stockoutPredictionService.createDemandForecast(tenantId, productId, storeId, horizonDays);
    },

    // ============================================
    // v3.0.0 - INVENTORY HEALTH
    // ============================================

    getInventoryHealthMetrics: async (
        productId: string,
        storeId: string,
        options?: { dateRange?: DateRange; limit?: number }
    ): Promise<InventoryHealthMetrics[]> => {
        return stockoutPredictionService.getInventoryHealthMetrics(productId, storeId, options);
    },

    getAtRiskInventory: async (
        tenantId: string,
        options?: { storeId?: string; riskType?: 'overstock' | 'understock' | 'deadstock' | 'at_risk'; limit?: number }
    ): Promise<InventoryHealthMetrics[]> => {
        return stockoutPredictionService.getAtRiskInventory(tenantId, options);
    },

    // ============================================
    // OPERATIONAL ALERTS
    // Task overdue, stale data, missing data alerts
    // ============================================

    /**
     * Refresh operational system alerts for a tenant.
     * Generates alerts for:
     * - Past due tasks
     * - Stale data (not updated in 15+ days)
     * - Missing data required for dashboard
     *
     * @param tenantId - Optional tenant ID. If not provided, refreshes all tenants.
     * @returns Summary of alerts generated by type
     */
    refreshSystemAlerts: async (
        tenantId?: string
    ): Promise<{
        taskOverdueCount: number;
        staleDataCount: number;
        missingDataCount: number;
        totalCount: number;
    }> => {
        try {
            const { data, error } = await supabase.rpc('refresh_system_alerts', {
                p_tenant_id: tenantId || null,
            });

            if (error) {
                // If function doesn't exist yet (migration not applied), log and return zeros
                if (error.code === '42883' || error.message?.includes('does not exist')) {
                    console.warn('[api.refreshSystemAlerts] Function not available yet - migration may not be applied');
                    return { taskOverdueCount: 0, staleDataCount: 0, missingDataCount: 0, totalCount: 0 };
                }
                // Permission denied (403) - user doesn't have EXECUTE permission
                if (error.code === 'PGRST301' || error.code === '42501' || (error as { status?: number }).status === 403) {
                    console.debug('[api.refreshSystemAlerts] Permission denied - skipping refresh');
                    return { taskOverdueCount: 0, staleDataCount: 0, missingDataCount: 0, totalCount: 0 };
                }
                throw error;
            }

            // Aggregate results from all tenants
            const results = Array.isArray(data) ? data : [];
            return {
                taskOverdueCount: results.reduce((sum: number, r: { task_overdue_count?: number }) => sum + (r.task_overdue_count || 0), 0),
                staleDataCount: results.reduce((sum: number, r: { stale_data_count?: number }) => sum + (r.stale_data_count || 0), 0),
                missingDataCount: results.reduce((sum: number, r: { missing_data_count?: number }) => sum + (r.missing_data_count || 0), 0),
                totalCount: results.reduce((sum: number, r: { total_count?: number }) => sum + (r.total_count || 0), 0),
            };
        } catch (error) {
            logErrorWithTrace('api.refreshSystemAlerts', error);
            return { taskOverdueCount: 0, staleDataCount: 0, missingDataCount: 0, totalCount: 0 };
        }
    },

    /**
     * Backfill daily_store_stats from historical transaction data.
     * Call this after importing historical transactions via CSV, since
     * the trigger only fires on new INSERTs.
     *
     * Invokes the DB function `backfill_daily_store_stats()` which
     * processes ALL tenants and recalculates stats from transactions.
     *
     * @returns Summary of stores and days processed
     */
    backfillDailyStoreStats: async (): Promise<{
        storesProcessed: number;
        daysProcessed: number;
    }> => {
        try {
            const { data, error } = await supabase.rpc('backfill_daily_store_stats', {});

            if (error) {
                // Function not found - migration may not be applied
                if (error.code === '42883' || error.message?.includes('does not exist')) {
                    console.warn('[api.backfillDailyStoreStats] Function not available yet - migration may not be applied');
                    return { storesProcessed: 0, daysProcessed: 0 };
                }
                // Permission denied
                if (error.code === 'PGRST301' || error.code === '42501' || (error as { status?: number }).status === 403) {
                    console.debug('[api.backfillDailyStoreStats] Permission denied - skipping backfill');
                    return { storesProcessed: 0, daysProcessed: 0 };
                }
                throw error;
            }

            // The function returns a summary or void - handle both cases
            if (data && typeof data === 'object' && !Array.isArray(data)) {
                return {
                    storesProcessed: (data as { stores_processed?: number }).stores_processed ?? 0,
                    daysProcessed: (data as { days_processed?: number }).days_processed ?? 0,
                };
            }

            // If function returns void/null, return generic success
            return { storesProcessed: -1, daysProcessed: -1 };
        } catch (error) {
            logErrorWithTrace('api.backfillDailyStoreStats', error);
            throw error;
        }
    },

    // ============================================
    // INVENTORY SNAPSHOTS
    // Generate point-in-time inventory accuracy snapshots
    // ============================================

    generateInventorySnapshot: async (
        tenantId: string,
        storeId: string,
        triggerType: string = 'manual',
        notes?: string
    ): Promise<{ snapshot_id: string; total_skus: number; ira: number | null; phantom_rate: number | null; doh: number | null; total_stock_value: number }> => {
        try {
            const { data, error } = await supabase.rpc('generate_inventory_snapshot', {
                p_tenant_id: tenantId,
                p_store_id: storeId,
                p_trigger_type: triggerType,
                p_notes: notes ?? null,
                p_created_by: null,
            });

            if (error) {
                if (error.code === '42883') {
                    throw new Error('Función generate_inventory_snapshot no encontrada. Verifique que la migración esté aplicada.');
                }
                if (error.code === '42501') {
                    throw new Error('Permisos insuficientes para generar snapshot de inventario.');
                }
                throw error;
            }

            return data as unknown as { snapshot_id: string; total_skus: number; ira: number | null; phantom_rate: number | null; doh: number | null; total_stock_value: number };
        } catch (error) {
            logErrorWithTrace('api.generateInventorySnapshot', error);
            throw error;
        }
    },

    generateAllInventorySnapshots: async (
        tenantId: string,
        triggerType: string = 'manual',
        notes?: string
    ): Promise<{ stores_processed: number; snapshots: Array<{ store_id: string; snapshot_id: string }> }> => {
        try {
            const { data, error } = await supabase.rpc('generate_all_inventory_snapshots', {
                p_tenant_id: tenantId,
                p_trigger_type: triggerType,
                p_notes: notes ?? null,
                p_created_by: null,
            });

            if (error) {
                // Bulk RPC not deployed — loop the per-store generate_inventory_snapshot
                // over the tenant's stores (same semantics as the bulk function).
                if (error.code === 'PGRST202' || error.code === '42883') {
                    const { data: stores, error: storesError } = await supabase
                        .from('stores')
                        .select('id')
                        .eq('tenant_id', tenantId);
                    if (storesError) throw storesError;

                    const snapshots: Array<{ store_id: string; snapshot_id: string }> = [];
                    for (const store of stores ?? []) {
                        const { data: snap, error: snapError } = await supabase.rpc('generate_inventory_snapshot', {
                            p_tenant_id: tenantId,
                            p_store_id: store.id,
                            p_trigger_type: triggerType,
                            p_notes: notes ?? null,
                            p_created_by: null,
                        });
                        if (snapError) throw snapError;
                        snapshots.push({
                            store_id: store.id,
                            snapshot_id: (snap as { snapshot_id: string }).snapshot_id,
                        });
                    }
                    return { stores_processed: snapshots.length, snapshots };
                }
                if (error.code === '42501') {
                    throw new Error('Permisos insuficientes para generar snapshots de inventario.');
                }
                throw error;
            }

            return data as unknown as { stores_processed: number; snapshots: Array<{ store_id: string; snapshot_id: string }> };
        } catch (error) {
            logErrorWithTrace('api.generateAllInventorySnapshots', error);
            throw error;
        }
    },

    // ============================================
    // v3.0.0 - KPI CALCULATION ENGINE
    // TASK-012 through TASK-018
    // ============================================

    // Financial KPIs (TASK-012)
    calculateGMROI: async (
        tenantId: string,
        options: { storeId?: string; productId?: string; dateRange?: DateRange }
    ): Promise<{ gmroi: number; grossMargin: number; avgInventoryCost: number }> => {
        const { KPIEngine } = await import('./kpiCalculationEngine');
        return KPIEngine.calculateGMROI(tenantId, options);
    },

    calculateGMROIByCategory: async (
        tenantId: string,
        dateRange: DateRange
    ): Promise<Array<{ category: string; gmroi: number; grossMargin: number }>> => {
        const { KPIEngine } = await import('./kpiCalculationEngine');
        return KPIEngine.calculateGMROIByCategory(tenantId, dateRange);
    },

    // Inventory Turnover (TASK-013)
    calculateInventoryTurnover: async (
        tenantId: string,
        options: { storeId?: string; period?: 'monthly' | 'quarterly' | 'yearly' }
    ): Promise<{ turnover: number; cogs: number; avgInventory: number }> => {
        const { KPIEngine } = await import('./kpiCalculationEngine');
        return KPIEngine.calculateInventoryTurnover(tenantId, options);
    },

    // Stockout Rate (TASK-014)
    calculateStockoutRate: async (
        tenantId: string,
        dateRange: DateRange
    ): Promise<{ rate: number; unfulfilledLines: number; totalLines: number }> => {
        const { KPIEngine } = await import('./kpiCalculationEngine');
        return KPIEngine.calculateStockoutRate(tenantId, dateRange);
    },

    getStockoutsByProduct: async (
        tenantId: string,
        dateRange: DateRange
    ): Promise<Array<{ productId: string; productName: string; stockoutCount: number; totalHours: number }>> => {
        const { KPIEngine } = await import('./kpiCalculationEngine');
        return KPIEngine.getStockoutsByProduct(tenantId, dateRange);
    },

    // On-Time Delivery (TASK-015)
    calculateOnTimeDeliveryRate: async (
        tenantId: string,
        options: { supplierId?: string; dateRange?: DateRange }
    ): Promise<{ rate: number; onTimeCount: number; totalCount: number; avgDelayHours: number }> => {
        const { KPIEngine } = await import('./kpiCalculationEngine');
        return KPIEngine.calculateOnTimeDeliveryRate(tenantId, options);
    },

    // Fulfillment Cycle Time (TASK-016)
    calculateFulfillmentCycleTime: async (
        tenantId: string,
        options: { storeId?: string; dateRange?: DateRange }
    ): Promise<{ avgCycleTimeHours: number; minHours: number; maxHours: number; orderCount: number }> => {
        const { KPIEngine } = await import('./kpiCalculationEngine');
        return KPIEngine.calculateFulfillmentCycleTime(tenantId, options);
    },

    // Alert MTTR (TASK-017)
    calculateAlertMTTR: async (
        tenantId: string,
        options: { alertType?: string; assigneeId?: string; dateRange?: DateRange }
    ): Promise<{ mttrHours: number; resolvedCount: number; byCategory: Array<{ category: string; mttrHours: number }> }> => {
        const { KPIEngine } = await import('./kpiCalculationEngine');
        return KPIEngine.calculateAlertMTTR(tenantId, options);
    },

    // False Positive Rate (TASK-018)
    calculateFalsePositiveRate: async (
        tenantId: string,
        options: { alertType?: string; dateRange?: DateRange }
    ): Promise<{ rate: number; falsePositiveCount: number; totalClosed: number; byType: Array<{ type: string; rate: number }> }> => {
        const { KPIEngine } = await import('./kpiCalculationEngine');
        return KPIEngine.calculateFalsePositiveRate(tenantId, options);
    },

    // Aggregated Dashboard KPIs
    getFinancialKPIs: async (
        tenantId: string,
        dateRange: DateRange
    ): Promise<{
        shrinkageRate: number;
        gmroi: number;
        shrinkageTrend: number;
        gmroiTrend: number;
        grossMargin: number;
        avgInventoryCost: number;
    }> => {
        const { KPIEngine } = await import('./kpiCalculationEngine');
        return KPIEngine.getFinancialKPIs(tenantId, dateRange);
    },

    getOperationalKPIs: async (
        tenantId: string,
        dateRange: DateRange
    ): Promise<{
        inventoryAccuracy: number;
        inventoryTurnover: number;
        stockoutRate: number;
        onTimeDelivery: number;
        orderFulfillmentCycleTime: number;
    }> => {
        const { KPIEngine } = await import('./kpiCalculationEngine');
        return KPIEngine.getOperationalKPIs(tenantId, dateRange);
    },

    getSystemKPIs: async (
        tenantId: string,
        dateRange: DateRange
    ): Promise<{
        alertMttr: number;
        falsePositiveRate: number;
        resolvedAlertCount: number;
        mttrByCategory: Array<{ category: string; mttrHours: number }>;
        fpByType: Array<{ type: string; rate: number }>;
    }> => {
        const { KPIEngine } = await import('./kpiCalculationEngine');
        return KPIEngine.getSystemKPIs(tenantId, dateRange);
    },

    getKPIDashboardData: async (
        tenantId: string,
        dateRange: DateRange
    ): Promise<{
        financial: Awaited<ReturnType<typeof api.getFinancialKPIs>>;
        operational: Awaited<ReturnType<typeof api.getOperationalKPIs>>;
        system: Awaited<ReturnType<typeof api.getSystemKPIs>>;
        lastUpdated: string;
    }> => {
        const { KPIEngine } = await import('./kpiCalculationEngine');
        return KPIEngine.getKPIDashboardData(tenantId, dateRange);
    },

    // ============================================
    // ALERT GENERATION
    // Trigger automated alert generation based on inventory metrics
    // ============================================

    /**
     * Refresh inventory quality alerts for a tenant.
     * Calls the database function that analyzes inventory metrics
     * and creates alerts when thresholds are breached.
     *
     * @param tenantId - Optional tenant ID. If not provided, refreshes all tenants.
     * @returns Summary of alerts generated
     */
    refreshInventoryAlerts: async (
        tenantId?: string
    ): Promise<{
        storesProcessed: number;
        alertsCreated: number;
        phantomDetected: number;
        cashierAnalyzed: number;
    }> => {
        try {
            const { data, error } = await supabase.rpc('refresh_all_inventory_alerts', {
                p_tenant_id: tenantId || null,
            });

            if (error) {
                // If function doesn't exist yet (migration not applied), log and return zeros
                if (error.code === '42883' || error.message?.includes('does not exist')) {
                    console.warn('[api.refreshInventoryAlerts] Function not available yet - migration may not be applied');
                    return { storesProcessed: 0, alertsCreated: 0, phantomDetected: 0, cashierAnalyzed: 0 };
                }
                // Permission denied (403) - user doesn't have EXECUTE permission
                if (error.code === 'PGRST301' || error.code === '42501' || (error as { status?: number }).status === 403) {
                    console.debug('[api.refreshInventoryAlerts] Permission denied - skipping refresh');
                    return { storesProcessed: 0, alertsCreated: 0, phantomDetected: 0, cashierAnalyzed: 0 };
                }
                throw error;
            }

            // Aggregate results from all stores
            const results = Array.isArray(data) ? data : [];
            return {
                storesProcessed: results.length,
                alertsCreated: results.reduce((sum: number, r: { alerts_created?: number }) => sum + (r.alerts_created || 0), 0),
                phantomDetected: results.reduce((sum: number, r: { phantom_detected?: number }) => sum + (r.phantom_detected || 0), 0),
                cashierAnalyzed: results.reduce((sum: number, r: { cashier_analyzed?: number }) => sum + (r.cashier_analyzed || 0), 0),
            };
        } catch (error) {
            logErrorWithTrace('api.refreshInventoryAlerts', error);
            return { storesProcessed: 0, alertsCreated: 0, phantomDetected: 0, cashierAnalyzed: 0 };
        }
    },

    /**
     * Trigger Control Tower data refresh (phantom inventory + cashier risk).
     * Only refreshes Control Tower analysis, not inventory quality alerts.
     */
    refreshControlTowerData: async (
        tenantId?: string
    ): Promise<{
        storesProcessed: number;
        phantomCount: number;
        cashierCount: number;
    }> => {
        try {
            const { data, error } = await supabase.rpc('refresh_control_tower_data', {
                p_tenant_id: tenantId || null,
            });

            if (error) {
                if (error.code === '42883' || error.message?.includes('does not exist')) {
                    console.warn('[api.refreshControlTowerData] Function not available');
                    return { storesProcessed: 0, phantomCount: 0, cashierCount: 0 };
                }
                throw error;
            }

            const results = Array.isArray(data) ? data : [];
            return {
                storesProcessed: results.length,
                phantomCount: results.reduce((sum: number, r: { phantom_count?: number }) => sum + (r.phantom_count || 0), 0),
                cashierCount: results.reduce((sum: number, r: { cashier_count?: number }) => sum + (r.cashier_count || 0), 0),
            };
        } catch (error) {
            logErrorWithTrace('api.refreshControlTowerData', error);
            return { storesProcessed: 0, phantomCount: 0, cashierCount: 0 };
        }
    },

    /**
     * DEBUG: Check what tenant context the RLS sees
     * Call this from browser console: api.debugTenantContext()
     */
    debugTenantContext: async (): Promise<{
        jwt_tenant_id: string | null;
        jwt_role: string | null;
        jwt_email: string | null;
        tenant_id_from_function: string | null;
    } | null> => {
        try {
            const { data, error } = await supabase.rpc('debug_tenant_context');
            if (error) {
                console.error('[DEBUG] Error:', error);
                return null;
            }
            console.log('[DEBUG] Tenant Context:', data);
            return data?.[0] || null;
        } catch (error) {
            console.error('[DEBUG] Exception:', error);
            return null;
        }
    },
};

// Expose api on window for debugging
if (typeof window !== 'undefined') {
    (window as unknown as { api: typeof api }).api = api;
}
